import { useMemo } from 'react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Chip,
  Spinner,
} from '@heroui/react';
import { Plus, Trash2, Pencil } from 'lucide-react';
import InlineEdit from '@/components/common/InlineEdit';
import { useMaterials, useCreateMaterial, useUpdateMaterial, useDeleteMaterial } from '@/api/materials';
import type { Material } from '@estimator/shared';

interface MaterialTableProps {
  projectId: string;
}

const UNIT_OPTIONS = [
  { value: 'm²', label: 'm²' },
  { value: 'm', label: 'm (linear)' },
  { value: 'lm', label: 'lm' },
  { value: 'nr', label: 'nr' },
  { value: 'item', label: 'item' },
  { value: 'set', label: 'set' },
  { value: 'ls', label: 'ls' },
];

function AiBadge() {
  return (
    <Chip
      size="sm"
      variant="flat"
      color="success"
      classNames={{
        base: 'h-4 min-w-0 px-1',
        content: 'text-[9px] font-ui font-bold px-0',
      }}
    >
      AI
    </Chip>
  );
}

function OverrideBadge() {
  return (
    <Chip
      size="sm"
      variant="flat"
      color="primary"
      classNames={{
        base: 'h-4 min-w-0 px-1',
        content: 'flex items-center gap-0.5 text-[9px] px-0',
      }}
    >
      <Pencil size={8} />
    </Chip>
  );
}

const CATEGORY_ACCENT: Record<string, string> = {
  Flooring: '#60a5fa',
  Walls: '#f97316',
  Ceiling: '#a78bfa',
  Joinery: '#34d399',
  Fixtures: '#f59e0b',
  External: '#fb7185',
};

function CategoryColor(category: string) {
  return CATEGORY_ACCENT[category] ?? 'var(--accent)';
}

export default function MaterialTable({ projectId }: MaterialTableProps) {
  const { data: materials, isLoading, isError } = useMaterials(projectId);
  const createMaterial = useCreateMaterial(projectId);
  const updateMaterial = useUpdateMaterial();
  const deleteMaterial = useDeleteMaterial();

  const grouped = useMemo(() => {
    if (!materials) return [];
    const map = new Map<string, Material[]>();
    for (const mat of materials) {
      const key = mat.category || 'Uncategorised';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(mat);
    }
    return Array.from(map.entries()).map(([category, rows]) => ({ category, rows }));
  }, [materials]);

  function handleUpdate(mat: Material, field: keyof Material, raw: string) {
    const numericFields = ['quantity'];
    const value = numericFields.includes(field)
      ? raw === '' ? null : parseFloat(raw)
      : raw;

    updateMaterial.mutate({
      id: mat.id,
      projectId,
      [field]: value,
      overridden: true,
    });
  }

  function handleAdd() {
    const category = grouped[0]?.category ?? 'Flooring';
    createMaterial.mutate({ name: 'New Material', category, unit: 'm²' });
  }

  function handleDelete(mat: Material) {
    if (confirm(`Delete material "${mat.name}"?`)) {
      deleteMaterial.mutate({ id: mat.id, projectId });
    }
  }

  const columns = [
    { key: 'name', label: 'Material' },
    { key: 'specification', label: 'Specification' },
    { key: 'location', label: 'Location' },
    { key: 'unit', label: 'Unit' },
    { key: 'quantity', label: 'Qty' },
    { key: 'notes', label: 'Notes' },
    { key: 'status', label: 'Status' },
    { key: 'actions', label: '' },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="md" color="primary" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-sm" style={{ color: 'var(--red)' }}>
          Failed to load materials.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
            Materials
          </h2>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
            {materials?.length ?? 0} materials — click any cell to edit inline
          </p>
        </div>
      </div>

      <div
        className="rounded-xl border overflow-hidden"
        style={{ borderColor: 'var(--border)' }}
      >
        <Table
          removeWrapper
          aria-label="Materials table"
          classNames={{
            table: 'min-w-full',
            th: 'bg-[var(--bg-base)] text-[10px] uppercase tracking-wider font-ui text-[var(--text-muted)] border-b border-[var(--border)] h-9 px-3',
            td: 'py-2 px-3 border-b border-[var(--border)] text-xs align-middle',
          }}
        >
          <TableHeader columns={columns}>
            {(col) => (
              <TableColumn key={col.key} className={col.key === 'actions' ? 'w-10' : ''}>
                {col.label}
              </TableColumn>
            )}
          </TableHeader>

          <TableBody
            emptyContent={
              <div className="py-12 text-center" style={{ color: 'var(--text-muted)' }}>
                No materials yet. Click Add Material below.
              </div>
            }
          >
            {grouped.flatMap(({ category, rows }, gi) => [
              // Category group header row
              <TableRow
                key={`cat-${gi}`}
                style={{ backgroundColor: 'var(--bg-base)' }}
              >
                {columns.map((col, ci) =>
                  ci === 0 ? (
                    <TableCell
                      key={col.key}
                      style={{ borderBottom: '1px solid var(--border-em)', paddingTop: '8px', paddingBottom: '8px' }}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block w-1.5 h-3.5 rounded-full"
                          style={{ backgroundColor: CategoryColor(category) }}
                        />
                        <span
                          className="text-xs font-semibold uppercase tracking-widest font-ui"
                          style={{ color: CategoryColor(category) }}
                        >
                          {category}
                        </span>
                        <span className="text-[10px] font-ui" style={{ color: 'var(--text-muted)' }}>
                          ({rows.length})
                        </span>
                      </div>
                    </TableCell>
                  ) : (
                    <TableCell
                      key={col.key}
                      style={{ borderBottom: '1px solid var(--border-em)' }}
                    >
                      {' '}
                    </TableCell>
                  )
                )}
              </TableRow>,

              // Data rows
              ...rows.map((mat, ri) => (
                <TableRow
                  key={mat.id}
                  style={{
                    backgroundColor: ri % 2 === 0 ? 'var(--bg-surface)' : 'var(--bg-panel)',
                  }}
                >
                  {/* Material name */}
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <InlineEdit
                        value={mat.name}
                        onSave={(v) => handleUpdate(mat, 'name', v)}
                      />
                      {mat.ai_extracted && !mat.overridden && <AiBadge />}
                      {mat.overridden && <OverrideBadge />}
                    </div>
                  </TableCell>

                  {/* Specification */}
                  <TableCell>
                    <InlineEdit
                      value={mat.specification}
                      onSave={(v) => handleUpdate(mat, 'specification', v)}
                      placeholder="—"
                    />
                  </TableCell>

                  {/* Location */}
                  <TableCell>
                    <InlineEdit
                      value={mat.location}
                      onSave={(v) => handleUpdate(mat, 'location', v)}
                      placeholder="—"
                    />
                  </TableCell>

                  {/* Unit */}
                  <TableCell>
                    <InlineEdit
                      value={mat.unit}
                      onSave={(v) => handleUpdate(mat, 'unit', v)}
                      type="select"
                      options={UNIT_OPTIONS}
                      className="font-ui"
                    />
                  </TableCell>

                  {/* Quantity */}
                  <TableCell>
                    <InlineEdit
                      value={mat.quantity != null ? Number(mat.quantity.toFixed(2)) : undefined}
                      onSave={(v) => handleUpdate(mat, 'quantity', v)}
                      type="number"
                      step={0.01}
                      min={0}
                      className="font-ui"
                    />
                  </TableCell>

                  {/* Notes */}
                  <TableCell>
                    <InlineEdit
                      value={mat.notes}
                      onSave={(v) => handleUpdate(mat, 'notes', v)}
                      placeholder="—"
                    />
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <Chip
                      size="sm"
                      variant="dot"
                      color={mat.ai_extracted && !mat.overridden ? 'success' : mat.overridden ? 'primary' : 'default'}
                      classNames={{
                        base: 'h-5 border-none',
                        content: 'text-[10px] font-ui px-1',
                      }}
                    >
                      {mat.overridden ? 'Edited' : mat.ai_extracted ? 'AI' : 'Manual'}
                    </Chip>
                  </TableCell>

                  {/* Delete */}
                  <TableCell>
                    <button
                      onClick={() => handleDelete(mat)}
                      className="p-1 rounded opacity-30 hover:opacity-100 transition-opacity focus:outline-none focus-visible:ring-1 focus-visible:ring-[var(--red)]"
                      style={{ color: 'var(--red)' }}
                      title="Delete material"
                    >
                      <Trash2 size={13} />
                    </button>
                  </TableCell>
                </TableRow>
              )),
            ])}
          </TableBody>
        </Table>
      </div>

      {/* Add Material */}
      <Button
        size="sm"
        variant="bordered"
        startContent={<Plus size={14} />}
        isLoading={createMaterial.isPending}
        onPress={handleAdd}
        className="border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--accent)] hover:text-[var(--accent)]"
      >
        Add Material
      </Button>
    </div>
  );
}
